import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import SearchIcon from '@mui/icons-material/Search';

/**
 * The search-and-filters card of a list page: the search field fills the first row and the
 * filters wrap underneath. With `onSubmit` the search is applied on Enter or the Search button
 * rather than on every keystroke.
 */
export function SearchFilterBar({
  label,
  helperText,
  value,
  onChange,
  onBlur,
  onSubmit,
  'aria-label': ariaLabel,
  actions,
  children,
}: {
  /** The search field's label. Omit for a card that holds filters only. */
  label?: string;
  helperText?: string;
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  onSubmit?: () => void;
  'aria-label'?: string;
  /** Buttons under the filters, for a card that is applied on request and has no search field. */
  actions?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <Paper
      component="form"
      aria-label={ariaLabel}
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit?.();
      }}
      sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}
    >
      {label !== undefined && (
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <TextField
            fullWidth
            label={label}
            helperText={helperText}
            value={value ?? ''}
            onChange={(event) => {
              onChange?.(event.target.value);
            }}
            onBlur={onBlur}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
            sx={{ '& .MuiOutlinedInput-root': { height: 46 } }}
          />
          {onSubmit !== undefined && (
            <Button type="submit" variant="contained" sx={{ height: 46, px: 3, flexShrink: 0 }}>
              Search
            </Button>
          )}
        </Box>
      )}
      {children !== undefined && children !== null && children !== false && (
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 200px), 1fr))',
            // A filter fills its cell: a minimum width set for a free-flowing row must not make it
            // spill over its neighbour here.
            '&& > *': { minWidth: 0 },
          }}
        >
          {children}
        </Box>
      )}
      {actions !== undefined && <Box sx={{ display: 'flex', gap: 1 }}>{actions}</Box>}
    </Paper>
  );
}
